import { useRef } from "react";
import { useMerchant } from "@auth";
import { Button, Checkbox, Input } from "@components";
import Tooltip from "@components/Tooltip";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { cn, extractTitle } from "@utils";
import {
  ArrowDown,
  ArrowUp,
  ChevronExpand,
  Search,
} from "react-bootstrap-icons";
import { Stat, SubStat, Filters, FiltersButton, ViewsManagement, DraggableHeaderRow } from ".";
import { shouldShowPinnedLeft } from "./pinnedLeftVisibility";
import SkeletonRows from "./SkeletonRows";

const TableCore = ({
  config,
  rows,
  rawData,
  managedColumns,
  filters,
  setFilters,
  addFilter,
  updateFilter,
  removeFilter,
  autoOpenKey,
  newFilterButtonRef,
  getColTitle,
  scope,
  sort,
  sortDirection,
  onSort,
  searchTerm,
  onSearchTermChange,
  isAllChecked,
  isRowChecked,
  isRowHighlighted,
  onSelectAllChanged,
  onSelectedChange,
  statistics,
  pagination,
  toolbarLeft,
  primaryHeaderAction,
  menu,
  headerAction,
  tableContainerRef,
  loading = false,
  onColumnReorder,
}) => {
  const { merchant, hasPermission } = useMerchant();
  const showTopToolbar = !!toolbarLeft || !!primaryHeaderAction;
  const tableScrollRef = useRef(null);

  const shouldShowCol = (x) => {
    return (
      !x.hide &&
      // filterOnly columns exist to be filtered and switched off, never drawn. They still reach
      // the preferences (ServerTable/ClientTable let them through), which is what puts them in
      // column customisation and the filter picker — unlike `hide`, which removes them outright.
      !x.filterOnly &&
      (!x.permission || hasPermission(x.permission)) &&
      !(merchant?.customisationSettings?.hiddenColumnKeys ?? []).includes(
        `${config.key}.${x.key}`,
      )
    );
  };

  const isRowSelectable = (row) =>
    !config.isSelectable || config.isSelectable(row);

  const visibleColumns = managedColumns.filter((x) => shouldShowCol(x));

  const showPinnedLeft = shouldShowPinnedLeft(config.pinnedLeft, managedColumns);

  // A sortable column's arrow is absolutely positioned against the right edge, so the cell has to
  // reserve room for it in its own padding: pr-8 clears the 4-wide icon and the pr-2 it sits in.
  // Without that the title runs underneath the arrow — which only showed up once a title was long
  // enough to wrap, or carried an info icon after it.
  const thClassName = (x) =>
    `sticky top-0 z-10 bg-white py-3.5 pl-3 ${x.sortFunc ? "pr-8" : "pr-3"} text-${
      x.right ? "right" : "left"
    } text-sm font-semibold border-b border-zinc-200 ${
      x.sortFunc ? "hover:bg-zinc-200" : ""
    }`;

  const thStyle = (x) => ({
    maxWidth: x.width || "200px",
    minWidth: x.width || "200px",
  });

  // titleTooltip stays separate from title: the column manager persists title and compares it by
  // identity, so it has to remain a plain string.
  const thTitle = (x) =>
    x.titleTooltip ? (
      <span className="inline-flex items-center gap-1 align-middle">
        {getColTitle(x)}
        <Tooltip
          content={x.titleTooltip}
          wrapperClassName="inline-flex shrink-0"
          placement="bottom"
          variant="card"
        >
          <InformationCircleIcon className="h-4 w-4 shrink-0 text-zinc-400" strokeWidth={2} />
        </Tooltip>
      </span>
    ) : (
      getColTitle(x)
    );

  const thContent = (x) => (
    <>
      <span>{thTitle(x)}</span>
      {x.sortFunc && (
        <span className="absolute inset-y-0 right-0 flex items-center pr-2">
          {x.key === sort ? (
            sortDirection === "desc" ? (
              <ArrowDown className="h-4 w-4 text-zinc-500" />
            ) : (
              <ArrowUp className="h-4 w-4 text-zinc-500" />
            )
          ) : (
            <ChevronExpand className="h-4 w-4 text-zinc-500" />
          )}
        </span>
      )}
    </>
  );

  const tableEl = (
    <div
      ref={(el) => {
        tableScrollRef.current = el;
        if (tableContainerRef) tableContainerRef.current = el;
      }}
      className="w-full flex-1"
      style={{ overflowX: "auto" }}
    >
      <table
        className="min-w-full w-full table-auto divide-y divide-zinc-300"
        style={{ borderCollapse: "separate", borderSpacing: 0 }}
      >
        <thead>
          {onColumnReorder ? (
            <DraggableHeaderRow
              visibleColumns={visibleColumns}
              showPinnedLeft={showPinnedLeft}
              onColumnReorder={onColumnReorder}
              config={config}
              isAllChecked={isAllChecked}
              onSelectAllChanged={onSelectAllChanged}
              onSort={onSort}
              thClassName={thClassName}
              thStyle={thStyle}
              thContent={thContent}
              tableScrollRef={tableScrollRef}
            />
          ) : (
            <tr>
              {config.selectable && (
                <th className="sticky top-0 left-0 bg-white z-20 border-b border-zinc-200 px-7 sm:w-12 sm:px-6">
                  <Checkbox
                    checked={isAllChecked}
                    onChange={() => onSelectAllChanged()}
                  />
                </th>
              )}
              {showPinnedLeft && (
                <th className="sticky top-0 left-0 bg-white z-20 border-b border-r border-zinc-200 px-4 w-12">
                  {config.pinnedLeft.header ?? null}
                </th>
              )}
              {visibleColumns.map((x, i) => (
                <th
                  key={i}
                  role={x.sortFunc ? "button" : ""}
                  onClick={() => x.sortFunc && onSort(x.key)}
                  style={thStyle(x)}
                  className={thClassName(x)}
                >
                  {thContent(x)}
                </th>
              ))}
              {config.rowActions && (
                <th className="sticky top-0 right-0 z-10 bg-white border-b border-l border-zinc-200 px-7 sm:w-12 sm:px-6" />
              )}
            </tr>
          )}
        </thead>
        {/* DIAGNOSTIC: fade-in removed to test the Safari not-painting-until-hover issue —
            opacity animation on a remounted <tbody> can be left unpainted by Safari. */}
        <tbody
          key={loading ? "skeleton" : "data"}
          className="bg-white"
        >
          {loading ? (
            <SkeletonRows
              columns={visibleColumns}
              selectable={config.selectable}
              hasPinnedLeft={showPinnedLeft}
              hasRowActions={!!config.rowActions}
            />
          ) : (
            rows &&
            rows.map((x, i) => {
              let checked = isRowChecked(x);
              return (
                <tr
                  key={i}
                  role={config.onClick ? "button" : ""}
                  className={cn(
                    "transition-colors hover:bg-zinc-100",
                    isRowHighlighted?.(x) ? "bg-violet-50" : checked ? "bg-zinc-50" : "bg-white",
                  )}
                >
                  {config.selectable && (
                    <td
                      className={`sticky h-16 left-0 z-10 px-7 sm:w-12 sm:px-6 bg-inherit${
                        i === rows.length - 1 ? "" : " border-b border-zinc-200"
                      }`}
                    >
                      {checked && (
                        <div className="absolute inset-y-0 left-0 w-0.5 bg-primary" />
                      )}
                      {isRowSelectable(x) && (
                        <Checkbox
                          checked={checked}
                          onChange={() => onSelectedChange(x)}
                        />
                      )}
                    </td>
                  )}
                  {showPinnedLeft && (
                    <td
                      className={`sticky h-14 left-0 z-10 px-4 w-12 bg-inherit border-r border-zinc-200 text-center${
                        i === rows.length - 1 ? "" : " border-b"
                      }`}
                      role={config.pinnedLeft.onClick ? "button" : ""}
                      onClick={() => config.pinnedLeft.onClick?.(x)}
                    >
                      {config.pinnedLeft.render(x)}
                    </td>
                  )}
                  {visibleColumns.map((y, j) => {
                    const cellContent = y.render(x);
                    const title = extractTitle(cellContent);
                    return (
                      <td
                        key={j}
                        title={title}
                        className={`h-14 max-h-14 whitespace-nowrap bg-inherit px-3 flex-block items-center text-sm${
                          i === rows.length - 1 ? "" : " border-b border-zinc-200"
                        } ${y.right && "text-right"}`}
                        style={{
                          maxWidth: y.width || "200px",
                          minWidth: y.width || "200px",
                          overflowX: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                        role={y.onClick ? "button" : ""}
                        onClick={() =>
                          y.onClick
                            ? y.onClick(x)
                            : config.onClick
                              ? y.preventRowClick
                                ? null
                                : config.onClick(x)
                              : null
                        }
                      >
                        {cellContent}
                      </td>
                    );
                  })}
                  {config.rowActions && (
                    <td
                      className={`h-14 bg-inherit px-3 text-sm border-l border-zinc-200 sticky right-0${
                        i === rows.length - 1 ? "" : " border-b"
                      }`}
                    >
                      <div className="flex gap-2">
                        {config.rowActions
                          .filter(
                            (y) =>
                              (!y.show || y.show(x)) &&
                              (!y.permission || hasPermission(y.permission)),
                          )
                          .map((y, i) => (
                            <Button
                              key={i}
                              color="link"
                              className="whitespace-nowrap"
                              onClick={() => y.onClick(x)}
                              title={y.title}
                              aria-label={y.title}
                            >
                              {y.icon && (
                                <y.icon
                                  className={`size-4${y.label ? " me-1" : ""}`}
                                />
                              )}
                              {y.label}
                            </Button>
                          ))}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {pagination && (
        <div className="flex items-center sticky z-10 bottom-0 left-0 bg-white px-3 py-4 text-sm border-b border-zinc-200 w-full border-t">
          <Button
            size="xs"
            onClick={pagination.onPrev}
            disabled={pagination.prevDisabled}
            className="mr-2"
          >
            previous
          </Button>
          <Button
            size="xs"
            onClick={pagination.onNext}
            disabled={pagination.nextDisabled}
            className="mr-4"
          >
            next
          </Button>
          <span>{pagination.label}</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex-1 flex flex-col h-full overflow-auto">
      {/* Top action bar */}
      {showTopToolbar && (
        <div className="flex-none flex px-4 py-3 gap-2 w-full border-b border-zinc-200 h-16 items-center">
          {toolbarLeft}
          {primaryHeaderAction && (
            <div className="ml-auto flex items-center gap-3">
              {primaryHeaderAction}
            </div>
          )}
        </div>
      )}

      {/* Statistics */}
      {statistics && (
        <div className="flex-none flex w-full border-b border-zinc-200 overflow-x-auto divide-x divide-zinc-300 px-5">
          {statistics.map((stat, i) => (
            <Stat key={i} label={stat.label} value={stat.value}>
              {stat.subStatistics &&
                stat.subStatistics.map((subStat, j) => (
                  <SubStat key={j} label={subStat.label} value={subStat.value} />
                ))}
            </Stat>
          ))}
        </div>
      )}

      {/* Filter / search / selection bar */}
      <div className="flex-none flex flex-col w-full border-b border-zinc-200">
        <div className="flex flex-none py-2 px-5 gap-2 w-full items-center">
          <FiltersButton
            filters={filters}
            managedColumns={managedColumns}
            onAddFilter={addFilter}
            getColTitle={getColTitle}
          />
          <ViewsManagement
            configKey={config.key}
            filters={filters}
            setFilters={setFilters}
            scope={scope}
          />
          <div className="flex flex-none ml-auto items-center gap-3">
            {headerAction}
            <Input
              prefix={<Search />}
              placeholder={config.searchPlaceholder || "Search"}
              value={searchTerm}
              onChange={onSearchTermChange}
              className="h-8 py-1"
            />
            {menu}
          </div>
        </div>
        <Filters
          data={rawData}
          filters={filters}
          columns={config.columns}
          managedColumns={managedColumns}
          autoOpenKey={autoOpenKey}
          newFilterButtonRef={newFilterButtonRef}
          onRemoveFilter={removeFilter}
          onUpdateFilter={updateFilter}
        />
      </div>

      {/* Table */}
      {tableEl}
    </div>
  );
};

export default TableCore;
